import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, ShieldCheck, LogIn, ChevronRight } from 'lucide-react';
import AuthLayout from '../components/AuthLayout';

const AdminLoginPage: React.FC = () => {
  const { t } = useTranslation('common');
  const { adminLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim() || !adminPassword.trim()) return;
    try {
      setLoading(true);
      const user = await adminLogin(email.trim(), password.trim(), adminPassword.trim());
      if (user) navigate('/admin');
    } catch {
      // toast handled in AuthContext
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title={t('auth.adminLoginTitle', 'دخول المدير')}
      subtitle={t('auth.adminLoginSubtitle', 'قم بتسجيل الدخول باستخدام بيانات الإدارة')}
    >
      <form className="space-y-6" onSubmit={handleSubmit} autoComplete="off">
        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">
            {t('auth.email')}
          </label>
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-primary-500 transition-colors">
              <Mail className="h-5 w-5" />
            </div>
            <input
              id="email"
              name="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-premium pl-12 py-3"
              placeholder="admin@openauction24.com"
              disabled={loading}
              autoComplete="off"
              dir="ltr"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="password" className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">
            {t('auth.password')}
          </label>
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-primary-500 transition-colors">
              <Lock className="h-5 w-5" />
            </div>
            <input
              id="password"
              name="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-premium pl-12 py-3"
              placeholder="••••••••"
              disabled={loading}
              autoComplete="new-password"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="adminPassword" className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">
            {t('auth.adminPassword', 'كلمة سر الإدارة')}
          </label>
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-primary-500 transition-colors">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <input
              id="adminPassword"
              name="adminPassword"
              type="password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              className="input-premium pl-12 py-3"
              placeholder="••••••••"
              disabled={loading}
              autoComplete="new-password"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading || !email.trim() || !password.trim() || !adminPassword.trim()}
          className="w-full bg-primary-600 hover:bg-primary-700 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-primary-500/20 active:scale-[0.98] transition-all disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center gap-2"
        >
          {loading ? (
            <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <LogIn className="w-5 h-5" />
              {t('auth.submitAdminLogin', 'دخول المدير')}
            </>
          )}
        </button>

        <div className="pt-4 text-center">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t('auth.notAdmin', 'لست مديراً؟')}{' '}
            <Link to="/login" className="font-bold text-primary-600 hover:text-primary-700 hover:underline inline-flex items-center gap-0.5 group">
              {t('auth.userLogin', 'دخول المستخدم')}
              <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </p>
        </div>
      </form>
    </AuthLayout>
  );
};

export default AdminLoginPage;
